import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { Category, Incident, Priority } from '../lib/types';
import { Banner, Field } from './ui';

/**
 * Incident intake.
 *
 * Two things shape this form. First, a call taker types it while someone is
 * talking, so the fields are in the order they are actually said: what, where,
 * who. Second, an incident can be saved as a draft or sent straight to
 * dispatch — the draft exists so a half-captured emergency is never lost when a
 * call drops mid-sentence.
 */

export interface IncidentDraft {
  categoryId: string | null;
  priorityId: string | null;
  callerName: string;
  callerPhone: string;
  callerRelationship: string;
  locationAddress: string;
  locationDetails: string;
  latitude: number | null;
  longitude: number | null;
  narrative: string;
}

export const emptyDraft: IncidentDraft = {
  categoryId: null,
  priorityId: null,
  callerName: '',
  callerPhone: '',
  callerRelationship: '',
  locationAddress: '',
  locationDetails: '',
  latitude: null,
  longitude: null,
  narrative: '',
};

export function IncidentForm({
  categories,
  priorities,
  prefill,
  callId,
  incident,
  onSaved,
  onCancel,
}: {
  categories: Category[];
  priorities: Priority[];
  prefill?: Partial<IncidentDraft>;
  /** Attaches the live call to the incident it produced. */
  callId?: string | null;
  /** When present the form edits instead of creating. */
  incident?: Incident;
  onSaved: (incident: Incident, sentToDispatch: boolean) => void;
  onCancel?: () => void;
}) {
  const [draft, setDraft] = useState<IncidentDraft>({ ...emptyDraft, ...prefill });
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  // A new caller identified mid-call replaces an untouched form; anything the
  // operator has already typed is left alone.
  useEffect(() => {
    if (!prefill) return;
    setDraft((current) => {
      const untouched =
        current.narrative === '' && current.locationAddress === '' && current.categoryId === null;
      return untouched ? { ...current, ...prefill } : current;
    });
  }, [prefill]);

  useEffect(() => {
    if (!incident) return;
    setDraft({
      categoryId: incident.category_id,
      priorityId: incident.priority_id,
      callerName: incident.caller_name ?? '',
      callerPhone: incident.caller_phone ?? '',
      callerRelationship: incident.caller_relationship ?? '',
      locationAddress: incident.location_address ?? '',
      locationDetails: incident.location_details ?? '',
      latitude: incident.latitude,
      longitude: incident.longitude,
      narrative: incident.narrative,
    });
  }, [incident]);

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)),
    [categories],
  );

  const set = <K extends keyof IncidentDraft>(key: K, value: IncidentDraft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  /** Choosing a category applies its default priority unless one was set by hand. */
  const chooseCategory = (categoryId: string) => {
    const category = categories.find((c) => c.id === categoryId);
    setDraft((current) => ({
      ...current,
      categoryId: categoryId || null,
      priorityId: current.priorityId ?? category?.default_priority_id ?? null,
    }));
  };

  const submit = async (status: 'draft' | 'pending') => {
    setBusy(true);
    setError(null);
    setFieldErrors({});
    const body = {
      categoryId: draft.categoryId,
      priorityId: draft.priorityId,
      callerName: draft.callerName || null,
      callerPhone: draft.callerPhone || null,
      callerRelationship: draft.callerRelationship || null,
      locationAddress: draft.locationAddress || null,
      locationDetails: draft.locationDetails || null,
      latitude: draft.latitude,
      longitude: draft.longitude,
      narrative: draft.narrative,
    };

    try {
      if (incident) {
        const result = await api.patch<{ incident: Incident }>(`/incidents/${incident.id}`, body);
        onSaved(result.incident, false);
      } else {
        const result = await api.post<{ incident: Incident }>('/incidents', { ...body, status, callId });
        setDraft({ ...emptyDraft });
        onSaved(result.incident, status === 'pending');
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFieldErrors(err.fieldErrors);
      } else {
        setError('Could not save the incident');
      }
    } finally {
      setBusy(false);
    }
  };

  const ready = Boolean(draft.categoryId && draft.locationAddress.trim() && draft.narrative.trim());

  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault();
        void submit('pending');
      }}
    >
      {error && <Banner kind="error">{error}</Banner>}

      <div className="columns two">
        <Field label="Type" error={fieldErrors.categoryId}>
          {(id) => (
            <select id={id} value={draft.categoryId ?? ''} onChange={(e) => chooseCategory(e.target.value)}>
              <option value="">Select…</option>
              {sortedCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field label="Priority" error={fieldErrors.priorityId}>
          {(id) => (
            <select id={id} value={draft.priorityId ?? ''} onChange={(e) => set('priorityId', e.target.value || null)}>
              <option value="">Select…</option>
              {priorities.map((priority) => (
                <option key={priority.id} value={priority.id}>
                  {priority.name}
                  {priority.dispatch_sla_minutes ? ` · dispatch within ${priority.dispatch_sla_minutes}m` : ''}
                </option>
              ))}
            </select>
          )}
        </Field>
      </div>

      <Field label="Location" error={fieldErrors.locationAddress}>
        {(id) => (
          <input
            id={id}
            type="text"
            placeholder="Street address or landmark"
            value={draft.locationAddress}
            onChange={(e) => set('locationAddress', e.target.value)}
          />
        )}
      </Field>

      <Field label="Location detail" hint="Floor, apartment, access notes, nearest junction.">
        {(id) => (
          <input
            id={id}
            type="text"
            value={draft.locationDetails}
            onChange={(e) => set('locationDetails', e.target.value)}
          />
        )}
      </Field>

      <div className="columns three">
        <Field label="Latitude">
          {(id) => (
            <input
              id={id}
              type="number"
              step="0.000001"
              value={draft.latitude ?? ''}
              onChange={(e) => set('latitude', e.target.value === '' ? null : Number(e.target.value))}
            />
          )}
        </Field>
        <Field label="Longitude">
          {(id) => (
            <input
              id={id}
              type="number"
              step="0.000001"
              value={draft.longitude ?? ''}
              onChange={(e) => set('longitude', e.target.value === '' ? null : Number(e.target.value))}
            />
          )}
        </Field>
        <Field label="Caller is">
          {(id) => (
            <input
              id={id}
              type="text"
              placeholder="Witness, victim, relative…"
              value={draft.callerRelationship}
              onChange={(e) => set('callerRelationship', e.target.value)}
            />
          )}
        </Field>
      </div>

      <div className="columns two">
        <Field label="Caller name">
          {(id) => (
            <input id={id} type="text" value={draft.callerName} onChange={(e) => set('callerName', e.target.value)} />
          )}
        </Field>
        <Field label="Caller number">
          {(id) => (
            <input id={id} type="tel" value={draft.callerPhone} onChange={(e) => set('callerPhone', e.target.value)} />
          )}
        </Field>
      </div>

      <Field label="What is happening" error={fieldErrors.narrative}>
        {(id) => (
          <textarea
            id={id}
            rows={5}
            placeholder="Free text — everything the caller reports."
            value={draft.narrative}
            onChange={(e) => set('narrative', e.target.value)}
          />
        )}
      </Field>

      <div className="row">
        <button className="btn primary" type="submit" disabled={busy || !ready}>
          {incident ? 'Save changes' : 'Send to dispatch'}
        </button>
        {!incident && (
          <button className="btn" type="button" disabled={busy} onClick={() => void submit('draft')}>
            Save as draft
          </button>
        )}
        {onCancel && (
          <button className="btn ghost" type="button" onClick={onCancel}>
            Cancel
          </button>
        )}
        {!ready && !incident && (
          <span className="faint">Type, location and description are needed before dispatch.</span>
        )}
      </div>
    </form>
  );
}
